// A lineup row is an act as the festival announced it: one artist, a b2b
// ("Deorro B2B DJ Diesel"), or a named collab (Skull Machine) whose members
// perform under `collabArtistId`. See plans/lineup-acts.md.

interface ActFields {
  artistId?: string
  artistIds?: string[]
  collabArtistId?: string
  title?: string
}
/** Typed docs, drafts and untyped snapshot children all qualify. */
type ActLike = ActFields | Record<string, unknown>

const fields = (entry: ActLike) => entry as ActFields

/** The performers, billing order. Legacy/solo lineup rows only have `artistId`. */
export function entryArtistIds(entry: ActLike): string[] {
  const { artistIds, artistId } = fields(entry)
  if (artistIds?.length) return artistIds
  return artistId ? [artistId] : []
}

/** Every artist whose followers care about this act: the performers plus the named collab. */
export function entryInterestIds(entry: ActLike): string[] {
  const ids = entryArtistIds(entry)
  const { collabArtistId } = fields(entry)
  return collabArtistId && !ids.includes(collabArtistId) ? [...ids, collabArtistId] : ids
}

/**
 * An act's identity: the named collab when it has one (so a legacy
 * `[desert-hearts]` row and a later members + `collabArtistId: 'desert-hearts'`
 * row are the same act), otherwise its sorted performers.
 */
export function actKey(entry: ActLike): string {
  return fields(entry).collabArtistId ?? [...entryArtistIds(entry)].sort().join('+')
}

/** True when the act names more than one performer. */
export function isMultiArtistAct(entry: ActLike): boolean {
  return entryArtistIds(entry).length > 1
}

/** How an act reads in plain text: its announced title, else the performers joined b2b. */
export function actBilling(entry: ActLike, nameOf: (artistId: string) => string): string {
  return fields(entry).title ?? entryArtistIds(entry).map(nameOf).join(' b2b ')
}
